import { useEffect, useRef } from "react";
import { Button } from "primereact/button"; // Import Button
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  setCurrentStep,
  setSelectedContract,
  setIsContractPopupOpened,
  setIsSystemsPopupOpened,
  save,
} from "src/redux/slices/assignBuildingSlice";
import "./AssignBuilding.scss";
import { useTranslation } from "react-i18next";
import { RoutePaths } from "src/resources/enum";
import { toast } from "react-toastify";
import { IBmcBuildingInfo } from "src/redux/interfaces/IAssignBuilding";
import { getInvoiceDetails } from "src/redux/slices/invoiceSlice";
import { generateInvoiceURLHandler } from "src/utils/common";
import _ from "lodash";
import PrintButton from "../PrintButton/PrintButton";
import { scrollToTopWithClass } from "src/utils/helpers";

const AssignBuildingFooter = (params: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation(["common", "user"]);

  const assignBuilding = useSelector((state: any) => state.assignBuilding);
  const bmcBuildingInfo = useSelector(
    (state: any) => state.assignBuilding.bmcBuildingInfo
  ) as IBmcBuildingInfo;
  const { invoiceID } = useSelector(
    (state: any) => state.invoices?.invoiceDetailsPopupData?.invoiceDetails
  );
  const { isContractPopupOpened, isSystemsPopupOpened } = useSelector(
    (state: any) => state.assignBuilding
  );
  const systemsChanged = useRef(null);

  const afterSuccess = (invoiceGuid: any) => {
    toast.success(t("dataSavedSuccessfully"));
    dispatch(setIsSystemsPopupOpened(false) as any);
    if (assignBuilding.isNewMode) {
      dispatch(getInvoiceDetails(invoiceGuid) as any).then(() => {
        navigate(`${RoutePaths.PayInvoice}?invoiceId=${invoiceGuid}`);
      });
    } else {
      dispatch(setIsContractPopupOpened(false) as any);
      dispatch(setIsSystemsPopupOpened(false) as any);
      if (
        params.current === "systems" &&
        invoiceGuid &&
        systemsChanged.current
      ) {
        dispatch(getInvoiceDetails(invoiceGuid) as any).then(() => {
          navigate(`${RoutePaths.PayInvoice}?invoiceId=${invoiceGuid}`);
        });
      }
    }
  };
  const afterFailure = (res: any) => {};
  useEffect(() => {
    dispatch(setCurrentStep(params.current));
  }, []);

  useEffect(() => {
    const className = "assign-building-title";
    scrollToTopWithClass(className, null);
  }, [dispatch]);
  
  function saveAssignedBuilding() {
    dispatch(save(afterSuccess, afterFailure) as any).then(() => {});
  }

  const next = () => {
    //save data to redux

    if (params.current === "details") {
      // dispatch(setBuildingDetails(params.buildingDetails));
      navigate(RoutePaths.BuildingContract);
    } else if (params.current === "contract") {
      params.onBackClick();
      if (params.onNextClick()) {
        if (
          !assignBuilding.isNewMode ||
          (assignBuilding.isNewMode && assignBuilding.isContractPopupOpened)
        ) {
          dispatch(setIsContractPopupOpened(false) as any);
          dispatch(setIsSystemsPopupOpened(true) as any);
          dispatch(setCurrentStep(params.nextPage));
        } else {
          navigate(RoutePaths.BuildingSystems);
        }
      }
    } else if (params.current === "systems") {
      const { isValid, isSystemsChanged } = params.onNextClick();
      systemsChanged.current = isSystemsChanged;
      if (isValid) {
        if (
          !assignBuilding.isNewMode ||
          (assignBuilding.isNewMode && assignBuilding.isSystemsPopupOpened)
        ) {
          saveAssignedBuilding();
        } else {
          navigate(RoutePaths.BuildingConsent);
        }
      }
    } else if (params.current === "consent") {
      if (params.onNextClick()) {
        saveAssignedBuilding();
      }
    } else if (params.current === "payment") {
      if (params.onNextClick()) {
        // create invoice
        generateInvoiceURLHandler(dispatch, invoiceID);
      }
    }
  };

  const back = () => {
    if (params.current === "details") {
      dispatch(setCurrentStep(""));
      navigate(RoutePaths.SearchForBuilding);
    }
    if (params.current === "contract") {
      params.onBackClick();
      navigate(RoutePaths.BuildingDetails);
    } else if (params.current === "systems") {
      params.onBackClick();
      if (assignBuilding.isNewMode && !assignBuilding.isSystemsPopupOpened) {
        navigate(RoutePaths.BuildingContract);
      } else {
        dispatch(setIsContractPopupOpened(true) as any);
        dispatch(setIsSystemsPopupOpened(false) as any);
        dispatch(setCurrentStep("contract"));
      }
    } else if (params.current === "consent") {
      navigate(RoutePaths.BuildingSystems);
    }
  };
  function getLabelText(): string | undefined {
    if (params.current === "payment") {
      return t("payNow");
    } else if (
      params.current !== "consent" &&
      !assignBuilding.isSystemsPopupOpened
    ) {
      return t("next");
    } else if (assignBuilding.isSystemsPopupOpened) {
      return t("save");
    } else {
      return t("submit&Pay");
    }
  }

  function shouldHidePreviousButton() {
    return (
      (params.current === "contract" && assignBuilding.isContractPopupOpened) ||
      params.current === "payment"
    );
  }
  const disableSaveButton = () => {
    if (params.current === "systems") {
      return !params.selectedSystems || params.selectedSystems.length === 0;
    } else if (params.current === "consent") {
      return !assignBuilding.isConsentChecked;
    }
    return false;
  };
  return (
    <div
      className={`d-flex align-items-center assign-footer ${
        params.current === "payment" ? "invoice-details-footer" : ""
      } ${
        shouldHidePreviousButton() && params.current !== "payment"
          ? "justify-content-end"
          : "justify-content-between"
      } ${
        isContractPopupOpened || isSystemsPopupOpened ? "edit-contract" : ""
      }`}
    >
      {!shouldHidePreviousButton() && (
        <Button
          label={t("back")}
          onClick={back}
          className="light-primary-btn d-flex gap-2"
          icon={<PlayArrowIcon className="previous-icon m-0" />}
        ></Button>
      )}
      {params.current === "payment" && (
        <PrintButton contentRef={params?.invoiceContentRef} />
      )}

      <Button
        disabled={disableSaveButton()}
        icon={<PlayArrowIcon className="m-0" />}
        label={getLabelText()}
        onClick={next}
        className="light-primary-btn d-flex flex-row-reverse next-btn d-flex gap-2"
      ></Button>
    </div>
  );
};

export default AssignBuildingFooter;
